import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';

import { Store } from '@ngrx/store';
import { Subject, combineLatest } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { AuthService } from '@auth/auth.service';
import * as authActions from '@auth/store/auth.actions';
import { DataService } from '@data/data.service';

import { ZGrid } from '@util/general/grid';
import { defaultGridOptions } from '@util/general/grid.util';

import * as gridColumnDefinitions from './change-customer-grid.columns';

@Component({
  selector: 'app-change-customer-grid',
  templateUrl: './change-customer-grid.component.html',
  styleUrls: ['./change-customer-grid.component.scss'],
})
export class ChangeCustomerGridComponent extends ZGrid implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();

  public selectedRow: any = null;

  columnDefs = gridColumnDefinitions.columnDefs;
  rowData: any = [];
  filter: any;

  constructor(public dataService: DataService, private store: Store, private authService: AuthService) {
    super(dataService);

    this.gridOptions = {
      ...defaultGridOptions,
      rowSelection: 'multiple',
      onSelectionChanged: (_event) => this.publishSelectedRow(),
      onRowDoubleClicked: (_event) => this.selectRow(),
    };
  }

  ngOnInit() {
    combineLatest([this.dataService.customerList$, this.authService.customerDialogFilter$])
      .pipe(
        filter(([data, _flt]) => !!data),
        map(([data, flt]) => [data.list ? data.list : [], flt]),
        map(([data, flt]) => this.filterData(data, flt)),
        takeUntil(this.destroy$)
      )
      .subscribe((data) => (this.rowData = data));

    this.authService.changeCustomerDialog$
      .pipe(
        filter((x) => !!x),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.clearSelection());

    this.authService.selectedCustomerRow$.pipe(takeUntil(this.destroy$)).subscribe((row) => (this.selectedRow = row));
  }

  ngAfterViewInit() {
    this.agGrid.columnApi.applyColumnState({ state: gridColumnDefinitions.defColumnPreferences });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  private filterData(data: any, flt: any) {
    if (!flt || !flt.custid) {
      return data;
    }

    const returnData = data.filter((x) => {
      const idCheck = x.key.toLowerCase().includes(flt.custid.toLowerCase());
      const nameCheck = x.value.toLowerCase().includes(flt.custid.toLowerCase());
      return idCheck || nameCheck;
    });

    return returnData;
  }

  private clearSelection() {
    this.agGrid.api.deselectAll();
    this.authService.selectedCustomerRow$.next(null);
    this.authService.changeCustomerShowing.next('grid');
    this.authService.changeCustomerFilterMode.next(false);
  }

  publishSelectedRow() {
    const node = this.agGrid.api.getSelectedNodes().length > 0 ? this.agGrid.api.getSelectedNodes()[0].data : null;
    this.authService.selectedCustomerRow$.next(node);
  }

  dismissDialog() {
    this.dataService.hideScreen$.next(false);
    this.authService.changeCustomerDialog$.next(false);
  }

  selectRow() {
    if (this.selectedRow) {
      this.store.dispatch(new authActions.ChangeCustomer(this.selectedRow.key));
      this.dismissDialog();
    }
  }
}
